import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { getObservations } from 'src/app/performance/store/selectors/performance.selector';
import Utils from 'src/app/utils';

import CycleUtils from '../../cycle-utils';
import { Cycle, Movement, PropagationCompany, Section } from '../../models/cycle.model';
import { CycleService } from '../../services/cycle.service';
import { setSelectedMovementId } from '../../store/actions/cycle.actions';
import {
  getPropagationCompanies,
  getSections,
  getSelectedCycle,
  getSelectedMovement,
} from '../../store/selectors/cycle.selectors';

export interface MovementItemDialogInputData extends AbstractFormDialogData {
  movementId?: number;
}
export interface MovementItemDialogResultData {
  movement: Movement;
}

@Component({
  selector: 'app-movement-item-dialog',
  templateUrl: './movement-item-dialog.component.html',
  styleUrls: ['./movement-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    AgrNumberDirective,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class MovementItemDialogComponent extends AbstractDialog<MovementItemDialogResultData> {
  id: number;
  maxAvailableAmount: number;

  readonly formGroup = new FormGroup({
    movementDate: new FormControl('', [Validators.required]),
    fromSection: new FormControl<Section>(undefined, [Validators.required]),
    fromPropagationCompany: new FormControl<PropagationCompany>(undefined, [Validators.required]),
    toSection: new FormControl<Section>(undefined, [Validators.required]),
    toPropagationCompany: new FormControl<PropagationCompany>(undefined, [Validators.required]),
    numberOfAnimals: new FormControl<number>(undefined, Validators.required),
  });

  readonly selectedCycle$ = this.store.select(getSelectedCycle);
  readonly observations$ = this.store.select(getObservations);
  readonly sections$ = this.store.select(getSections);
  readonly cycleStartDate$ = this.store.select(getSelectedCycle).pipe(
    take(1),
    map((selectedCycle) => this.cycleService.getCycleStartDate(selectedCycle)),
  );

  private readonly currentState$: Observable<Record<string, number>> = this.store
    .select(getSelectedCycle)
    .pipe(
      withLatestFrom(this.observations$),
      take(1),
      map(([selectedCycle, observations]) => {
        const currentCycle: Cycle = {
          ...selectedCycle,
          movements: selectedCycle.movements.filter((m) => (this.id ? m.id < this.id : true)),
        };
        const currentMovement = selectedCycle.movements.find((m) => m.id === this.id);
        const filteredObservations = currentMovement
          ? observations.filter(
              (o) =>
                CycleUtils.getDateOfDayInCycle(currentCycle, o.day) <
                Utils.toUtcDate(new Date(currentMovement.movementDate)),
            )
          : observations;
        try {
          const animalTransitions = CycleUtils.getAnimalTransitions(
            currentCycle,
            filteredObservations,
          );
          const state = CycleUtils.getState(animalTransitions);

          return state instanceof Error ? undefined : state;
        } catch (_) {
          return undefined;
        }
      }),
    );

  readonly filteredFromSections$ = this.sections$.pipe(
    withLatestFrom(this.currentState$),
    map(([sections, currentState]) => {
      const fromSections: Section[] = [];
      for (const key in currentState) {
        if (Object.prototype.hasOwnProperty.call(currentState, key) && currentState[key] > 0) {
          const keySplitted = key.split('_');
          if (
            keySplitted.length === 2 &&
            sections.findIndex((s) => s.id.toString() === keySplitted[0]) >= 0 &&
            fromSections.findIndex((s) => s.id.toString() === keySplitted[0]) < 0
          ) {
            fromSections.push(sections.find((s) => s.id.toString() === keySplitted[0]));
          }
        }
      }
      return fromSections;
    }),
  );
  readonly filteredToSections$: BehaviorSubject<Section[]> = new BehaviorSubject([]);
  readonly filteredFromPropagationCompanies$: BehaviorSubject<PropagationCompany[]> =
    new BehaviorSubject([]);
  readonly propagationCompanies$ = this.store.select(getPropagationCompanies);
  selectedFromSection: Section = undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MovementItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      MovementItemDialogComponent,
      AbstractDialogResultData<MovementItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly cycleService: CycleService,
  ) {
    super();
    this.id = this.data.movementId;

    if (this.id > 0) {
      this.data.title = this.localeService.translate('movement.edit-movement');

      this.store.dispatch(setSelectedMovementId({ id: this.id }));
      this.store
        .select(getSelectedMovement)
        .pipe(
          withLatestFrom(this.sections$, this.propagationCompanies$),
          skipWhile(
            ([movement, sections, propagationCompanies]) =>
              movement === null ||
              movement === undefined ||
              sections?.length === 0 ||
              propagationCompanies?.length === 0,
          ),
          take(1),
        )
        .subscribe(([movement, sections, propagationCompanies]) => {
          this.formGroup.controls.movementDate.setValue(movement.movementDate);
          this.formGroup.controls.fromSection.setValue(
            sections.find((p) => p.id === movement.fromSection.id),
          );
          this.updateDependingControlsBasedOnFromSection(this.formGroup.controls.fromSection.value);
          this.filteredFromPropagationCompanies$
            .pipe(
              withLatestFrom(this.filteredToSections$),
              skipWhile(
                ([fromPropagationCompanies, toSections]) =>
                  fromPropagationCompanies?.length === 0 || toSections?.length === 0,
              ),
              take(1),
            )
            .subscribe(([fromPropagationCompanies, toSections]) => {
              this.formGroup.controls.fromPropagationCompany.setValue(
                fromPropagationCompanies.find((p) => p.id === movement.fromPropagationCompany.id),
              );
              this.updateDependingControlsBasedOnFromPropagationCompany(
                this.formGroup.controls.fromPropagationCompany.value,
              );
              this.formGroup.controls.toSection.setValue(
                toSections.find((p) => p.id === movement.toSection.id),
              );
              this.formGroup.controls.toPropagationCompany.setValue(
                propagationCompanies.find((p) => p.id === movement.toPropagationCompany.id),
              );
              this.formGroup.controls.numberOfAnimals.setValue(movement.numberOfAnimals);
            });
        });
    } else {
      this.data.title = this.localeService.translate('movement.add-movement');
    }
  }

  save() {
    if (this.formGroup.valid) {
      const movement: Movement = {
        id: this.id,
        movementDate:
          typeof (<unknown>this.formGroup.controls.movementDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.movementDate.value)).toISOString()
            : <string>this.formGroup.controls.movementDate.value,
        fromSection: this.formGroup.controls.fromSection.value,
        fromPropagationCompany: this.formGroup.controls.fromPropagationCompany.value,
        toSection: this.formGroup.controls.toSection.value,
        toPropagationCompany: this.formGroup.controls.toPropagationCompany.value,
        numberOfAnimals: this.formGroup.controls.numberOfAnimals.value,
      };
      this.confirm({
        movement,
      });
    }
  }

  updateDependingControlsBasedOnFromSection(fromSectionChanged: Section) {
    this.sections$
      .pipe(withLatestFrom(this.propagationCompanies$, this.currentState$), take(1))
      .subscribe(([sections, propagationCompanies, currentState]) => {
        const filteredFromPropagationComapnies: PropagationCompany[] = [];
        for (const key in currentState) {
          if (Object.prototype.hasOwnProperty.call(currentState, key) && currentState[key] > 0) {
            const keySplitted = key.split('_');
            if (keySplitted.length === 2) {
              if (fromSectionChanged.id.toString() === keySplitted[0]) {
                const correspondingPropagationCompany = propagationCompanies.find(
                  (p) => p.id.toString() === keySplitted[1],
                );
                if (correspondingPropagationCompany) {
                  filteredFromPropagationComapnies.push(correspondingPropagationCompany);
                }
              }
            }
          }
        }

        this.filteredFromPropagationCompanies$.next(filteredFromPropagationComapnies);

        const filteredToSections: Section[] = sections.filter(
          (s) =>
            s.breed.id === fromSectionChanged.breed.id && s.gender === fromSectionChanged.gender,
        );
        this.filteredToSections$.next(filteredToSections);
      });
  }

  fromSectionChanged($event: MatSelectChange) {
    this.formGroup.controls.fromPropagationCompany.setValue(undefined);
    this.formGroup.controls.toSection.setValue(undefined);
    this.formGroup.controls.toPropagationCompany.setValue(undefined);
    this.formGroup.controls.numberOfAnimals.setValue(undefined);
    this.updateDependingControlsBasedOnFromSection($event.value);
  }

  updateDependingControlsBasedOnFromPropagationCompany(propagationCompany: PropagationCompany) {
    if (propagationCompany) {
      const key = `${this.formGroup.controls.fromSection.value.id}_${propagationCompany.id}`;
      this.currentState$.pipe(take(1)).subscribe((currentState) => {
        const maxAmountAvailable = currentState[key];
        this.maxAvailableAmount = maxAmountAvailable;
        this.formGroup.controls.numberOfAnimals.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(maxAmountAvailable),
        ]);
      });
    }
  }

  fromPropagationCompanyChanged($event: MatSelectChange) {
    this.updateDependingControlsBasedOnFromPropagationCompany($event.value);
  }
}
